import React from 'react';
import { Grid, Alert, ToggleField, Button, ButtonSizeType } from '../common';
import { ButtonBar, TreeNavigation, TreeNavigationItem } from '@emorg-prd/standard-react';
import { Icon } from '../common/components/Icon';
import { AbstractPORForm, AbstractPORFormState, AbstractPORFormProps, IPORStateForm } from './AbstractPORForm';
import WorkflowDetailWrapper from '../common/wrappers/WorkflowDetailWrapper';
import PortOpeningRequest from '../../models/entities/PortOpeningRequest';
import { PORTypeInput, EnvironmentInput, ApproverInput, RulesTable, ApproverDelegationInput } from './components';
import { TenantLabel } from '../../models/enums/TenantLabel';
import UserLookup from '../common/form-controls/Lookups/UserLookup';
import TextAreaField from '../common/form-controls/TextareaField';
import { Validation } from '../../utils/Validation';
import ReadOnlyField from '../common/form-controls/ReadOnly';
import ConfirmModal from '../common/modals/ConfirmModal';
import WarningModal from '../common/modals/WarningModal';
import UpdatePortOpeningRequest from '../../models/viewModels/PortOpeningRequests/UpdatePortOpeningRequest';
import UpdatePortOpeningRequestRule from '../../models/viewModels/PortOpeningRequests/UpdatePortOpeningRequestRule';
import { WorkflowState } from '../../models/enums/WorkflowState';
import PortOpeningRequestsCreate from './PortOpeningRequestsCreate';
import { PortOpeningRequestsDirection } from '../../models/enums/POR/PortOpeningRequestsDirection';
import './PortOpeningRequestsDetail.css';
import { VirtualNetwork } from '../../models/entities/AzureResources/VirtualNetwork';
import { getVnets } from '../common/VirtualNetworksStorage';
import ProgressBarBig from './components/ProgressBarBig';
import { useParams } from 'react-router-dom';
import RulesAttestationTable from './components/RulesAttestationTable';
import { Loader } from '../common/components/Loader';
import SelectField from '../common/form-controls/SelectField';

interface PortOpeningRequestsDetailWithParamsProps extends AbstractPORFormProps {}

interface StateForm extends IPORStateForm {}

interface PortOpeningRequestsDetailWithParamsState extends AbstractPORFormState<StateForm> {
	loading: boolean;
	portOpeningRequest?: PortOpeningRequest;
	editMode: boolean;
	matchPatch: string;

	decisionModalVisible: {
		approve?: boolean;
		escalate?: boolean;
		reject?: boolean;
		review?: boolean;
		cancel?: boolean;
	};
	modalComment: string;
	subVirtualNetworks: VirtualNetwork[];
	selectedFromExisting: string[];
}

class PortOpeningRequestsDetailWithParams extends AbstractPORForm<
	PortOpeningRequestsDetailWithParamsProps,
	PortOpeningRequestsDetailWithParamsState
> {
	constructor(props) {
		super(props);

		this.state = {
			loading: true,
			stateForm: {
				environment: null,
				contact: null,
				isEmergency: false,
				customerCoordination: null,
				businessJustification: '',
				ruleList: [],
				hasDelegatedApprover: null,
				custodianApproval: null,
				cartaId: null,
			},
			editMode: false,
			submissionWarned: false,
			operationInProgress: false,
			endModal: {
				visible: false,
			},
			submissionAlert: {
				visible: false,
			},

			decisionModalVisible: {},
			modalComment: '',
			subVirtualNetworks: [],
			selectedFromExisting: [],
			matchPatch: null,
		};

		this.initFormModel();
	}

	initFormModel() {
		super.initFormModel();
		this.formModel.fields.approverId.validation = new Validation({
			required: () =>
				this.state.portOpeningRequest?.direction === PortOpeningRequestsDirection.SubscriptionToInternet && this.state.portOpeningRequest?.appEndorser.id == null,
		});

		this.formModel.fields.appEndorserUPN.validation = new Validation({
					required: () => this.state.portOpeningRequest?.direction === PortOpeningRequestsDirection.SubscriptionToInternet && this.state.portOpeningRequest?.approvingUser == null,
		});

		this.formModel.fields.portOpeningRules.getValue = () =>
			this.state.stateForm.ruleList.map((rule) => new UpdatePortOpeningRequestRule(rule));
	}

	async componentWillMount() {
		const id = parseInt(this.props.params.porId);
		const subsId = parseInt(this.props.params.subsId);
		await this.loadRequest(id, subsId);
	}

	loadRequest = async (id, subsId) => {
		this.setState(
			{
				loading: true,
			},
			async () => {
				try {
					const subscription = await this.getSubscriptionWithPOR(subsId, id);
					const request = new PortOpeningRequest(
						subscription.portOpeningRequests?.find((por: { id: number }) => por.id == id)
					);

					const subVirtualNetworks: VirtualNetwork[] = subscription.azureState?.toLowerCase() === 'enabled'
						? await getVnets(subscription.createdSubscriptionId,subscription.tenant.name)
						: [];

					if (
						request?.direction.value === PortOpeningRequestsDirection.OnPremToInternet.value ||
						request?.direction.value === PortOpeningRequestsDirection.NECTUpdate.value
					) {
						window.location.href = `https://ssppor-portal-prd.azurewebsites.net/PortOpeningRequests/${request?.id}`;
					}

					this.setState(
						{
							subscription,
							subVirtualNetworks,
							portOpeningRequest: request,
							//loading: false,
						},
						this.resetStateForm
					);

					this.setState({
						selectedFromExisting: this.state.stateForm.ruleList.map((rule) => rule.name),
					});

					if (request.direction === PortOpeningRequestsDirection.SubscriptionToInternet && request.approvingUser == null)
					{
						request.workflowInstance.currentWorkflowInstanceState.workflowState ==
													WorkflowState.Review && request.isEditable()
													? await this.handleAppEndorserOptions(subscription?.cartaIds?.[0]?.cartaAppId)
													: this.loadOptions(request);
					}
					this.setState({
						loading: false
					});
				} catch (error) {
					const HTTP_FORBIDDEN_ERROR_CODE = 403;
					if (error.status === HTTP_FORBIDDEN_ERROR_CODE) {
						this.handleEndModal(
							'You are not allowed to see this request.',
							'error',
							5000,
							'/subscriptions'
						);
					} else {
						this.handleEndModal('The request could not be retrieved.', 'error', 10000, '/subscriptions');
					}
				}
			}
		);
	};

	resetStateForm = () => {
		this.setState((prevState) => ({
			stateForm: this.stateFormFromRequest(prevState.portOpeningRequest),
		}));
	};

	loadOptions = (por: PortOpeningRequest) => {
			const options = [];			
			switch (por.direction) {
				case PortOpeningRequestsDirection.SubscriptionToInternet:
					options.push({
						value: por.appEndorser.customFields.contact_email,
						text: por.appEndorser.customFields.first_name + ' ' + por.appEndorser.customFields.last_name,
					});
					this.setState({ appEndorserOptions: options });
					break;
			}
		};

	stateFormFromRequest(por: PortOpeningRequest): StateForm {
		const currentState =
			this.state.portOpeningRequest.workflowInstance.currentWorkflowInstanceState.workflowState.value;
		if (currentState === WorkflowState.Review.value || currentState === WorkflowState.Draft.value) {
			let obj = super.stateFormFromRequest(por);
			return {
				...obj,
				ruleList: obj.ruleList.map((rule) => {
					return { ...rule, azureName: undefined };
				}),
			};
		}
		return super.stateFormFromRequest(por);
	}

	setSelectedFromExisting = (rulesName: string[]) => {
		this.setState({
			selectedFromExisting: rulesName,
		});
	};

	actions = () =>
		!this.state.operationInProgress &&
		!this.state.portOpeningRequest?.isAttestation && (
			<TreeNavigation optionalClass="em-u-clickable link-tree-por">
				{this.state.portOpeningRequest?.isEditable() && (
					<>
						<TreeNavigationItem
							label="Edit"
							optionalClass="em-c-btn em-c-btn--secondary em-c-btn--small"
							onClick={this.enableEdit}
						/>
						<br />
						<br />
					</>
				)}
				{(this.state.portOpeningRequest?.canHaveApplicationApproval() ||
					this.state.portOpeningRequest?.canHaveDOAGApproval() ||
					this.state.portOpeningRequest?.canHaveTechnicalDecision() ||
					this.state.portOpeningRequest?.canHaveSubscriptionOwnerDecision(
						this.state.subscription,
						this.state.portOpeningRequest?.custodianApproval
					)) && (
					<>
						<TreeNavigationItem
							label="Approve"
							optionalClass="em-c-btn em-c-btn--secondary em-c-btn--small"
							onClick={() => {
								this.openDecisionModal('approve');
							}}
						/>
						<br />
						<br />
						<TreeNavigationItem
							label="Reject"
							optionalClass="em-c-btn em-c-btn--secondary em-c-btn--small"
							onClick={() => {
								this.openDecisionModal('reject');
							}}
						/>
						<br />
						<br />
					</>
				)}
				{this.state.portOpeningRequest?.canHaveEscalateDecision() && (
					<>
						<TreeNavigationItem
							label={
								this.state.portOpeningRequest?.workflowInstance.currentWorkflowInstanceState
									.workflowState.value == WorkflowState.PendingForPolicyTeamApproval.value ||
								this.state.portOpeningRequest?.workflowInstance.currentWorkflowInstanceState
									.workflowState.value == WorkflowState.PendingForCloudNetworkApproval.value
									? 'Escalate To Solution Architect'
									: 'Escalate to Cyber Security'
							}
							optionalClass="em-c-btn em-c-btn--secondary em-c-btn--small"
							onClick={() => {
								this.openDecisionModal('escalate');
							}}
						/>
						<br />
						<br />
					</>
				)}
				{this.state.portOpeningRequest?.isCancellable() && (
					<>
						<TreeNavigationItem
							label="Cancel Request"
							optionalClass="em-c-btn em-c-btn--secondary em-c-btn--small"
							onClick={() => {
								this.openDecisionModal('cancel');
							}}
						/>
						<br />
						<br />
					</>
				)}
				{(this.state.portOpeningRequest?.canHaveTechnicalDecision() ||
					this.state.portOpeningRequest?.canSendToReview(
						this.state.subscription,
						this.state.portOpeningRequest?.workflowInstance
					)) && (
					<TreeNavigationItem
						label="Send to Review"
						optionalClass="em-c-btn em-c-btn--secondary em-c-btn--small"
						onClick={() => {
							this.openDecisionModal('review');
						}}
					/>
				)}
			</TreeNavigation>
		);

	editActions = (size: ButtonSizeType) => (
		<ButtonBar>
			{!this.state.operationInProgress && (
				<Button size={size} variant="secondary" onClick={this.cancelEdit}>
					<Icon size={size} icon={'x-filled'} className="em-c-save-icon" />
					<span>Cancel</span>
				</Button>
			)}
			<div className="em-c-btn-bar__item--separator" />
			<Button
				size={size}
				variant="secondary"
				disabled={!this.formModel.isValid() || this.state.operationInProgress}
				onClick={this.handleSave}
			>
				{this.state.operationInProgress ? (
					<>
						<Loader className="icon-loader em-c-icon--small" />
						<span>Loading...</span>
					</>
				) : (
					<>
						<Icon size={size} icon={'save-disk'} className="em-c-save-icon" />
						<span>Finish Review</span>
					</>
				)}
			</Button>
		</ButtonBar>
	);

	enableEdit = async () => {
		this.setState({
			editMode: true,
		});
	};

	cancelEdit = () => {
		this.setState({ editMode: false });
		this.resetStateForm();
	};

	openDecisionModal = (type: 'approve' | 'reject' | 'escalate' | 'review' | 'cancel') => {
		this.setState({
			decisionModalVisible: {
				[type]: true,
			},
		});
	};

	closeDecisionModal = () => {
		this.setState({
			decisionModalVisible: {},
			modalComment: '',
		});
	};

	setEmbedAlertSuccess = (message) => {
		this.setState(
			{
				submissionAlert: {
					variant: 'success',
					text: message,
					visible: true,
				},
			},
			() => {
				setTimeout(this.handleSubmissionAlertClose, 5000);
			}
		);
	};

	decisionModalConfirmationField = () => ({
		label: 'Please, provide the reason.',
		value: this.state.modalComment,
		validation: {
			required: true,
		},
		onChange: (ev) => {
			this.setState({
				modalComment: ev.target.value,
			});
		},
	});

	handleSave = () => {
		const operation = async () => {
			if (this.requiresApproverVerification(this.state.portOpeningRequest?.direction.name) && this.state.portOpeningRequest?.approvingUser != null)
			{
				const isVerified = await this.approverLevelVerified();
				if (!isVerified) return;
			}
			const model = this.formModel.create(UpdatePortOpeningRequest);
			await this.portOpeningRequestService.update(this.state.portOpeningRequest?.id, model);
			this.setState({
				editMode: false,
			});
		};

		this.handleOperationConfirmed({
			operation,
			inProgressMessage: 'Updating request...',
			successMessage: 'Your request was updated successfully.',
		});
	};

	handleApprove = async () => {
		this.handleOperationConfirmed({
			operation: async () => {
				await this.portOpeningRequestService.approve(this.state.portOpeningRequest?.id, {
					reason: this.state.modalComment,
				});
			},
			inProgressMessage: 'Approving...',
			successMessage: 'Request approved sucessfully.',
		});
	};

	handleEscalate = async () => {
		this.handleOperationConfirmed({
			operation: async () => {
				await this.portOpeningRequestService.escalate(this.state.portOpeningRequest?.id, {
					reviewReason: this.state.modalComment,
				});
			},
			inProgressMessage: 'Escalating...',
			successMessage: 'Request escalated sucessfully.',
		});
	};

	handleReject = async () => {
		this.handleOperationConfirmed({
			operation: async () => {
				await this.portOpeningRequestService.reject(this.state.portOpeningRequest?.id, {
					rejectionReason: this.state.modalComment,
				});
			},
			inProgressMessage: 'Rejecting...',
			successMessage: 'Request rejected sucessfully.',
		});
	};

	handleReview = async () => {
		this.handleOperationConfirmed({
			operation: async () => {
				await this.portOpeningRequestService.sendToReview(this.state.portOpeningRequest?.id, {
					reviewReason: this.state.modalComment,
				});
			},
			inProgressMessage: 'Sending to review...',
			successMessage: 'Request sent to review successfully.',
		});
	};

	handleCancellation = async () => {
		this.handleOperationConfirmed({
			operation: async () => {
				await this.portOpeningRequestService.cancel(this.state.portOpeningRequest?.id, {
					cancellationReason: this.state.modalComment,
				});
			},
			inProgressMessage: 'Cancelling...',
			successMessage: 'Request cancelled successfully.',
		});
	};

	handleOperationConfirmed = async ({ operation, inProgressMessage, successMessage }) => {
		this.setSubmissionAlertInProgress(inProgressMessage);
		this.closeDecisionModal();
		try {
			await operation();
			await this.loadRequest(this.state.portOpeningRequest?.id, this.state.subscription?.id);
			this.setEmbedAlertSuccess(successMessage);
		} catch (err) {
			const HTTP_INTERNAL_SERVER_ERROR_CODE = 500;
			if (err.status === HTTP_INTERNAL_SERVER_ERROR_CODE && err?.body?.message) {
				await this.loadRequest(this.state.portOpeningRequest?.id, this.state.subscription?.id);
			}
			this.setSubmissionAlertError(
				err?.body?.message ?? 'An error occured when processing your request. Please, try again later.'
			);
		} finally {
			this.setState({
				operationInProgress: false,
				modalComment: '',
			});
		}
	};

	render() {
		return (!this.state.loading &&
			this.state.portOpeningRequest != null &&
			this.state.portOpeningRequest?.isEditable() &&
			this.state.portOpeningRequest?.workflowInstance.currentWorkflowInstanceState.workflowState.value ===
				WorkflowState.Draft.value) ||
			(this.state.portOpeningRequest?.isAttestation &&
				this.state.portOpeningRequest?.workflowInstance.currentWorkflowInstanceState.workflowState.value ===
					WorkflowState.PendingAttestation.value) ? (
			<PortOpeningRequestsCreate
				draftRequest={this.state.portOpeningRequest}
				validNets={this.state.subVirtualNetworks}
			/>
		) : (
			<>
				<WorkflowDetailWrapper
					title={
						this.state.portOpeningRequest?.isAttestation == true
							? `Attestation Request #${this.state.portOpeningRequest?.id}`
							: `Port Opening Request #${this.state.portOpeningRequest?.id}`
					}
					workflowInstance={this.state.portOpeningRequest?.workflowInstance}
					loading={this.state.loading}
					linkTree={this.getLinkTree()}
					actions={this.state.editMode ? this.editActions('small') : this.actions()}
					fromPOR
					showHistoryButton
					currentApprovers={this.state.portOpeningRequest?.currentApprovers(this.state.subscription)}
				>
					{this.state.submissionAlert.visible && (
						<Alert
							variant={this.state.submissionAlert.variant}
							onClose={this.handleSubmissionAlertClose}
							body={this.state.submissionAlert.text}
						/>
					)}
					<Grid variant="3up">
						<Grid.Item>
							<PORTypeInput
								scope={{
									value: this.state.portOpeningRequest?.direction.name,
									disabled: true,
								}}
							/>
						</Grid.Item>
						{this.state.subscription?.tenant.id === TenantLabel.ExxonMobilTest && (
							<Grid.Item>
								<EnvironmentInput
									{...this.stateFormHandler().environment}
									disabled={this.state.operationInProgress || !this.state.editMode}
								/>
							</Grid.Item>
						)}
						{this.state.portOpeningRequest?.implementationTicket() !== '' && (
							<Grid.Item>
								<ReadOnlyField
									label="Implementation Ticket"
									text={
										this.state.portOpeningRequest?.workflowInstance?.currentWorkflowInstanceState
											?.ticketId
									}
									copy
								/>
							</Grid.Item>
						)}
					</Grid>
					{!this.state.portOpeningRequest?.isAttestation && (
						<Grid variant="halves">
							<Grid.Item>
								<UserLookup
									label="Technical contact"
									{...this.stateFormHandler().contact}
									disabled={this.state.operationInProgress || !this.state.editMode}
								/>
							</Grid.Item>
							{(this.state.portOpeningRequest?.direction.name !==
								PortOpeningRequestsDirection.SubscriptionToOnPrem.name) &&
								(
									//this.state.portOpeningRequest?.approvingUser !== null ?
									this.state.portOpeningRequest?.appEndorser.id == null ?
									<Grid.Item>
										<ApproverInput
											{...this.stateFormHandler().approver}
											disabled={this.state.operationInProgress || !this.state.editMode}
											scope={this.state.portOpeningRequest?.direction.name}
										/>
									</Grid.Item>
								:
									<Grid.Item>
										<SelectField
												label="Application Endorser"
												className="full-width"
												{...this.stateFormHandler().appEndorser}
												disabled={
													this.state.operationInProgress ||
													this.stateFormHandler().appEndorser.options.length == 0 ||
													!this.state.editMode
												}
												isLoading={this.stateFormHandler().appEndorser.options.length == 0}									
										/>
									</Grid.Item>
								)
							}
						</Grid>
					)}
					{this.state.portOpeningRequest?.direction.name !==
						PortOpeningRequestsDirection.SubscriptionToOnPrem.name &&
						!this.state.portOpeningRequest?.isAttestation && this.state.portOpeningRequest?.approvingUser !== null &&
						(
							<ApproverDelegationInput
								hasApproverDelegation={this.stateFormHandler().hasDelegatedApprover}
								delegatedApprover={this.stateFormHandler().delegatedApprover}
								disabled={this.state.operationInProgress || !this.state.editMode}
								isRequired={
									this.state.portOpeningRequest?.direction.name !==
									PortOpeningRequestsDirection.SubscriptionToOnPrem.name
								}
							/>
						)
					}
					<Grid variant="halves">
						<Grid.Item>
							<ToggleField
								id="account-type"
								{...this.stateFormHandler().custodianApproval}
								disabled={this.state.operationInProgress || !this.state.editMode}
								value={this.stateFormHandler().custodianApproval.value?.toString()}
								options={
									!this.state.operationInProgress && this.state.editMode
										? [
												{ value: 'true', text: 'Yes' },
												{ value: 'false', text: 'No' },
											]
										: this.stateFormHandler().custodianApproval?.value == 'true'
											? [{ value: 'true', text: 'Yes' }]
											: [{ value: 'false', text: 'No' }]
								}
							/>
						</Grid.Item>
						<Grid.Item>
							<ProgressBarBig
								workflowState={
									this.state.portOpeningRequest?.workflowInstance.currentWorkflowInstanceState
										.workflowState
								}
								porScope={this.state.portOpeningRequest?.direction.name}
								isAttestation={this.state.portOpeningRequest?.isAttestation}
								por={this.state.portOpeningRequest}
							/>
						</Grid.Item>
					</Grid>
					<TextAreaField
						label="Business Justification"
						{...this.stateFormHandler().businessJustification}
						disabled={this.state.operationInProgress || !this.state.editMode}
					/>
					{this.state.portOpeningRequest?.isAttestation ? (
						<RulesAttestationTable
							{...this.stateFormHandler().ruleset}
							porDirection={this.state.portOpeningRequest?.direction}
							subscriptionRequestName={this.state.subscription?.createdSubscriptionName}
							subscriptionRequestId={this.state.subscription?.id}
							readonly={true}
							validVirtualNetworks={this.state.subVirtualNetworks}
							fromDetails
						/>
					) : (
						<RulesTable
							{...this.stateFormHandler().ruleset}
							porDirection={this.state.portOpeningRequest?.direction}
							subscriptionRequestName={this.state.subscription?.createdSubscriptionName}
							readonly={this.state.operationInProgress || !this.state.editMode}
							validVirtualNetworks={this.state.subVirtualNetworks}
							loadingImplementedRules={false}
							currentWorkflowState={
								this.state.portOpeningRequest?.workflowInstance.currentWorkflowInstanceState
									.workflowState.value
							}
							fromDetails
							selectedFromExisting={this.state.selectedFromExisting}
							setSelectedFromExisting={this.setSelectedFromExisting}
						/>
					)}
					{this.state.editMode && this.editActions('medium')}
				</WorkflowDetailWrapper>
				<WarningModal {...this.state.endModal} willTimeout={false} onClose={this.endAndRedirect} />
				<ConfirmModal
					visible={this.state.decisionModalVisible.approve}
					title={`Approve POR #${this.state.portOpeningRequest?.id}`}
					question="Are you sure you want to approve this request?"
					confirmationField={this.decisionModalConfirmationField()}
					onConfirm={this.handleApprove}
					onCancel={this.closeDecisionModal}
				/>
				<ConfirmModal
					visible={this.state.decisionModalVisible.escalate}
					title={`Escalate POR #${this.state.portOpeningRequest?.id}`}
					question={`Are you sure you want to escalate to 
							${
								this.state.portOpeningRequest?.workflowInstance.currentWorkflowInstanceState
									.workflowState.value == WorkflowState.PendingForPolicyTeamApproval.value
									? 'Network Policy Solution Architect'
									: 'Cyber Security'
							} this request?`}
					confirmationField={this.decisionModalConfirmationField()}
					onConfirm={this.handleEscalate}
					onCancel={this.closeDecisionModal}
				/>
				<ConfirmModal
					visible={this.state.decisionModalVisible.cancel}
					title={`Cancel POR #${this.state.portOpeningRequest?.id}`}
					question="Are you sure you want to cancel this request? This action cannot be undone."
					confirmationField={this.decisionModalConfirmationField()}
					confirmButton={{
						label: 'Cancel Request',
						props: {
							variant: 'inverted-red',
						},
					}}
					onConfirm={() => this.handleCancellation()}
					onCancel={this.closeDecisionModal}
				/>
				<ConfirmModal
					visible={this.state.decisionModalVisible.reject}
					title={`Reject POR #${this.state.portOpeningRequest?.id}`}
					question="Are you sure you want to reject this request?"
					confirmationField={this.decisionModalConfirmationField()}
					onConfirm={() => this.handleReject()}
					onCancel={this.closeDecisionModal}
				/>
				<ConfirmModal
					visible={this.state.decisionModalVisible.review}
					title={`Confirm Revision for POR #${this.state.portOpeningRequest?.id}`}
					question="Are you sure you want to send this request to review?"
					confirmationField={this.decisionModalConfirmationField()}
					onConfirm={() => this.handleReview()}
					onCancel={this.closeDecisionModal}
				/>
			</>
		);
	}
}

const PortOpeningRequestsDetail = () => {
	return <PortOpeningRequestsDetailWithParams params={useParams()} />;
};

export default PortOpeningRequestsDetail;
